import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { isAuthenticated } from "../../auth";
import { getCheckinLines, prepareCheckinCheckout, completeCheckin } from "../../auth/apiCartCheckin";
import { toImageUrl } from "../../../config";
import imageEmptyelakay from "../../Layout/imgs/imageEmptyelakay.jpeg";
import Vote, { ratingVote } from "../commentaire/vote";
import Checkout from "../CheckoutProd";
import { elakayAutoText } from "../dataElakay";
import useShippingEstimate, { buildEstimateItems, buildEstimateDestination } from "../shippingEstimate/useShippingEstimate";
import { ShippingCostValue, SHIPPING_BLOCKING_SOURCES, blockedLineMessage } from "../shippingEstimate/ShippingEstimateSummary";
import {
  lineItemsSubtotal, itemsTotal, roundMoney, formatMoney, selectShippingEstimate, unitPriceOf,
} from "../checkoutTotals";
import { quoteCheckoutAPI } from "../../auth/paymentsApi";
import { TaxQuoteValue, TaxBasisNote, formatUsdCents, taxErrorReason } from "../checkoutTaxDisplay";
import "./CartCheckin.css";

const T = (key, fallback, values) => elakayAutoText(key, fallback, values);

const ISSUE_TEXT = {
  UNAVAILABLE: () => T("h_ckin_issue_unavailable", "No longer available"),
  VARIANT_UNAVAILABLE: () => T("h_ckin_issue_variant", "This option is no longer available"),
  OUT_OF_STOCK: () => T("h_ckin_issue_out_of_stock", "Out of stock"),
  INSUFFICIENT_STOCK: (line) => T("h_ckin_issue_insufficient", "Only {stock} left — update the quantity in your cart", { stock: line.quantity }),
  INVALID_QUANTITY: () => T("h_ckin_issue_quantity", "Invalid quantity"),
  CUSTOMIZATION_REQUIRED: () => T("h_ckin_issue_customization", "Needs customization — buy it from the product page"),
  // Refused by the server checkout quote: its own reason.
  QUOTE_REFUSED: (line) => line.refusedReason || T("h_ckin_issue_unavailable", "No longer available"),
};
const issueText = (line) => (ISSUE_TEXT[line.issue] || ISSUE_TEXT.UNAVAILABLE)(line);

// Customer-facing options only: "__cjVariantId", "__cjSku"... are internal
// supplier metadata kept in the variant filter (same rule as the bundles' filters).
const visibleOptions = (filter) => Object.fromEntries(Object.entries(filter || {})
  .filter(([k, v]) => !k.startsWith("__") && v !== undefined && v !== null && String(v).trim() !== ""));

// Fields used only by this modal: never part of a checkout line.
const DISPLAY_ONLY = ["eligible", "issue", "itemsCents", "unitPriceCents", "bundleNotice", "bundleGroupId", "vote"];

const Spinner = () => <span className="cki-spinner" aria-hidden="true" />;

// One cart line's Shipping cell: the same component (and texts) the Checkout
// summary uses, on the backend estimate narrowed to this line.
const LineShipping = ({ estimate, index }) => {
  if (index < 0) return <span className="cki-muted">—</span>;
  return <ShippingCostValue estimate={selectShippingEstimate(estimate, [index])} />;
};

const SelectBox = ({ checked, indeterminate = false, disabled = false, onChange, label }) => {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.indeterminate = indeterminate; }, [indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      className="cki-checkbox"
      checked={checked}
      disabled={disabled}
      onChange={onChange}
      aria-label={label}
      aria-checked={indeterminate ? "mixed" : checked}
    />
  );
};

// CART -> Check-in -> choose the lines to pay now -> the EXISTING Checkout
// (CheckoutProd, the same modal the Combined Offer "Buy Now" uses) with only
// those lines. The cart page itself is not changed.
const CartCheckin = ({ infoSource, Saddress, setSAddressFinal, laguangeElakay }) => {
  const { token } = isAuthenticated() || {};
  const [open, setOpen] = useState(false);
  const [state, setState] = useState({ status: "idle", lines: [] });
  const [selected, setSelected] = useState(null); // Set of cartItemIds; null = not chosen yet
  const [notice, setNotice] = useState(null);
  const [pageNotice, setPageNotice] = useState(null);
  const [preparing, setPreparing] = useState(false);
  const [checkout, setCheckout] = useState(null);
  const [quote, setQuote] = useState({ status: "idle", data: null });

  // Lines the server checkout quote refused (details.lines): shown with the
  // server's reason and no longer selectable, so the others keep their quote.
  const [refused, setRefused] = useState(() => new Map());
  const lines = useMemo(() => state.lines.map((l) => (refused.has(l.cartItemId)
    ? { ...l, eligible: false, issue: "QUOTE_REFUSED", refusedReason: refused.get(l.cartItemId) }
    : l)), [state.lines, refused]);
  const eligible = useMemo(() => lines.filter((l) => l.eligible), [lines]);

  const load = useCallback(async () => {
    if (!token) return;
    setRefused(new Map());
    setState((s) => ({ ...s, status: "loading" }));
    const r = await getCheckinLines(token);
    if (!r?.success) {
      setState({ status: "error", lines: [], error: r?.error });
      return;
    }
    setState({ status: "ready", lines: r.lines || [] });
    // First opening: every purchasable line; afterwards the buyer's choice is kept.
    setSelected((prev) => {
      const ok = new Set((r.lines || []).filter((l) => l.eligible).map((l) => l.cartItemId));
      return prev ? new Set([...prev].filter((cid) => ok.has(cid))) : ok;
    });
  }, [token]);

  const openModal = () => {
    setNotice(null);
    setPageNotice(null);
    setOpen(true);
    load();
  };

  // One estimate for every purchasable line (shipping is priced per line).
  const estimateItems = useMemo(() => (open ? buildEstimateItems(eligible) : []), [open, eligible]);
  const estimate = useShippingEstimate({ items: estimateItems, destination: buildEstimateDestination(Saddress), token });
  const indexOf = useMemo(() => new Map(eligible.map((l, i) => [l.cartItemId, i])), [eligible]);

  const chosen = selected || new Set();
  const selectedLines = eligible.filter((l) => chosen.has(l.cartItemId));
  const selectedIndexes = selectedLines.map((l) => indexOf.get(l.cartItemId));
  const selectedShipping = selectShippingEstimate(estimate, selectedIndexes);
  const shippingReady = selectedShipping.status === "ready";
  const blockedLine = shippingReady ? (selectedShipping.data.lines || []).find((l) => SHIPPING_BLOCKING_SOURCES.has(l.cost?.source)) : null;

  // Tax and the total come from the SAME server quote the Checkout charges
  // (POST /order/quote) for exactly the selected lines and this address --
  // never from a tax rate computed here.
  const quoteBody = open && selectedLines.length
    ? JSON.stringify({
      products: selectedLines.map((l) => {
        const line = { ...l };
        DISPLAY_ONLY.forEach((k) => delete line[k]);
        return line;
      }),
      shippingAddress: Saddress,
    })
    : "";
  const quotedIdsRef = useRef([]);
  quotedIdsRef.current = selectedLines.map((l) => l.cartItemId);
  useEffect(() => {
    if (!quoteBody) { setQuote({ status: "idle", data: null }); return undefined; }
    const quotedIds = quotedIdsRef.current;
    const controller = new AbortController();
    setQuote({ status: "loading", data: null });
    const timer = setTimeout(() => {
      quoteCheckoutAPI(JSON.parse(quoteBody), { signal: controller.signal })
        .then((data) => setQuote({ status: "ready", data }))
        .catch((err) => {
          if (err.name === "AbortError") return;
          const lineRefusals = (err.details?.lines || []).filter((l) => quotedIds[l.index]);
          if (lineRefusals.length) {
            const ids = lineRefusals.map((l) => quotedIds[l.index]);
            setRefused((prev) => new Map([...prev, ...lineRefusals.map((l) => [quotedIds[l.index], l.error])]));
            setSelected((prev) => new Set([...(prev || [])].filter((cid) => !ids.includes(cid))));
          }
          // A shipping refusal still carries the tax (details.tax).
          setQuote({ status: "error", data: null, error: taxErrorReason(err.code) || err.message, code: err.code, tax: err.details?.tax || null });
        });
    }, 300);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [quoteBody]);
  const quoteReady = quote.status === "ready";
  // Server quote line of a selected cart line (same order as sent).
  const quoteLineOf = (line) => {
    const k = selectedLines.findIndex((l) => l.cartItemId === line.cartItemId);
    return quoteReady && k >= 0 ? quote.data.lines?.[k] : null;
  };
  // Tax of a selected line when the quote was refused for shipping.
  const refusedTaxLineOf = (line) => {
    const k = selectedLines.findIndex((l) => l.cartItemId === line.cartItemId);
    return quote.status === "error" && k >= 0 ? quote.tax?.lines?.[k] || null : null;
  };

  const itemTotal = quoteReady ? quote.data.itemsCents / 100 : itemsTotal(selectedLines);
  const total = quoteReady ? quote.data.amountCents / 100 : roundMoney(0);

  const allSelected = eligible.length > 0 && eligible.every((l) => chosen.has(l.cartItemId));
  const someSelected = selectedLines.length > 0 && !allSelected;

  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(eligible.map((l) => l.cartItemId)));
  // A combined offer is paid as a whole: its lines move together.
  const toggleLine = (line) => setSelected((prev) => {
    const next = new Set(prev || []);
    const group = line.bundleGroupId ? eligible.filter((l) => l.bundleGroupId === line.bundleGroupId) : [line];
    const on = !next.has(line.cartItemId);
    group.forEach((l) => (on ? next.add(l.cartItemId) : next.delete(l.cartItemId)));
    return next;
  });

  const mergeLines = (fresh = []) => {
    const byId = new Map(fresh.map((l) => [l.cartItemId, l]));
    setState((s) => ({ ...s, lines: s.lines.map((l) => byId.get(l.cartItemId) || l) }));
    setSelected((prev) => new Set([...(prev || [])].filter((cid) => !byId.has(cid) || byId.get(cid).eligible)));
  };

  const startCheckout = async () => {
    if (!selectedLines.length || preparing) return;
    setPreparing(true);
    setNotice(null);
    const expectedItemsCents = selectedLines.reduce((s, l) => s + (Number(l.itemsCents) || 0), 0);
    const r = await prepareCheckinCheckout(token, selectedLines.map((l) => l.cartItemId), expectedItemsCents);
    setPreparing(false);
    if (!r?.success) {
      if (r?.code === "PRICE_CHANGED" && r.details?.lines) {
        mergeLines(r.details.lines);
        setNotice({ type: "warn", text: T("h_ckin_price_changed", "Prices changed since you opened Check-in. The summary has been updated — please review it.") });
      } else if (r?.code === "CHECKIN_LINES_UNAVAILABLE" && r.details?.lines) {
        mergeLines(r.details.lines);
        setNotice({ type: "warn", text: T("h_ckin_lines_unavailable", "Some products can no longer be purchased and were unselected. Please review your selection.") });
      } else {
        setNotice({ type: "error", text: r?.error || T("h_ckin_prepare_failed", "Checkout could not be prepared. Please try again.") });
        load();
      }
      return;
    }
    const products = (r.lines || []).map((l) => {
      const line = { ...l };
      DISPLAY_ONLY.forEach((k) => delete line[k]);
      return {
        ...line,
        displayFilters: visibleOptions(l.filter),
        paymentIntent: { customer: infoSource?.userInfo?.StripeCustomerId, payment_method: null },
      };
    });
    setOpen(false);
    setCheckout({ key: Date.now(), products });
  };

  const onOrderSuccess = async (response) => {
    setCheckout(null);
    const orderId = response?._id;
    const done = orderId ? await completeCheckin(token, orderId) : null;
    if (token && typeof infoSource?.cartItems === "function") infoSource.cartItems(token);
    setState({ status: "idle", lines: [] });
    setSelected(null);
    const kept = done?.success ? (done.kept || []).length : 0;
    setPageNotice({
      type: kept ? "warn" : "success",
      text: kept
        ? T("h_ckin_order_partial", "{count} product(s) remain in your cart. Your order is saved.", { count: kept })
        : T("h_ckin_order_success", "Order placed. The ordered products were removed from your cart."),
    });
  };

  if (!token) return null;

  const headerLabels = {
    product: T("h_ckin_col_product", "Product"),
    itemPrice: T("h_ckin_col_item_price", "Item Price"),
    shipping: T("h_ckin_col_shipping", "Shipping"),
    tax: T("h_ckin_col_tax", "Tax"),
    total: T("h_ckin_col_total", "Total"),
  };

  const renderRow = (line) => {
    const index = indexOf.get(line.cartItemId) ?? -1;
    const isChosen = chosen.has(line.cartItemId);
    const subtotal = lineItemsSubtotal(line);
    const quoted = isChosen ? quoteLineOf(line) : null;
    const refusedTax = isChosen && !quoted ? refusedTaxLineOf(line) : null;
    const lineShipping = selectShippingEstimate(estimate, index >= 0 ? [index] : []);
    const lineShipReady = lineShipping.status === "ready";
    // Product with an error: cannot be bought now, or cannot ship to this destination.
    const hasError = !line.eligible || (lineShipReady && (lineShipping.data.lines || []).some((l) => SHIPPING_BLOCKING_SOURCES.has(l.cost?.source)));
    const votes = line.vote || [];
    const rating = votes.length ? ratingVote(votes) : 0;
    return (
      <tr key={line.cartItemId} className={`cki-row${isChosen ? " is-selected" : ""}${line.eligible ? "" : " is-disabled"}${hasError ? " has-error" : ""}`}>
        <td className="cki-cell cki-cell--check">
          <SelectBox
            checked={isChosen}
            disabled={!line.eligible}
            onChange={() => toggleLine(line)}
            label={T("h_ckin_select_product", "Select {product}", { product: line.name })}
          />
        </td>
        <td className="cki-cell cki-cell--image">
          <img
            className="cki-thumb"
            src={toImageUrl(line.urlImg) || imageEmptyelakay}
            alt={line.name}
            loading="lazy"
            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = imageEmptyelakay; }}
          />
        </td>
        <td className="cki-cell cki-cell--product">
          <div className="cki-name">{line.name}</div>
          <div className="cki-rating">
            <Vote key={`${line.cartItemId}-${rating}`} rating={rating} starSize="14px" />
            <span className="cki-votes">({votes.length})</span>
          </div>
          {Object.keys(visibleOptions(line.filter)).length > 0 && (
            <div className="cki-options">{Object.entries(visibleOptions(line.filter)).map(([k, v]) => `${k}: ${v}`).join(" • ")}</div>
          )}
          {line.bundleNotice && (
            <div className="cki-badge">
              {T("h_bnd_cart_badge", "Combined offer")}{line.bundleNotice.name ? `: ${line.bundleNotice.name}` : ""}
              {line.bundleNotice.applied
                ? ` · ${T("h_ckin_bundle_whole", "selected as a whole")}`
                : ` · ${T("h_bnd_cart_not_applied", "This offer no longer applies; regular prices are shown.")}`}
            </div>
          )}
          {!line.eligible && <div className="cki-issue" role="note">{issueText(line)}</div>}
        </td>
        <td className="cki-cell cki-cell--money cki-cell--itemprice" data-label={headerLabels.itemPrice}>
          {line.eligible ? (
            <span className="cki-money">
              <span className="cki-amount">{formatMoney(subtotal)}</span>
              <span className="cki-unit">{T("h_ckin_unit_x_qty", "{price} × {count}", { price: formatMoney(unitPriceOf(line)), count: line.count })}</span>
            </span>
          ) : <span className="cki-muted">—</span>}
        </td>
        <td className="cki-cell cki-cell--money" data-label={headerLabels.shipping}>
          {line.eligible ? <LineShipping estimate={estimate} index={index} /> : <span className="cki-muted">—</span>}
        </td>
        <td className="cki-cell cki-cell--money" data-label={headerLabels.tax}>
          {!line.eligible || !isChosen ? <span className="cki-muted">—</span>
            : quoted ? <TaxQuoteValue quote={{ ...quote, data: { ...quote.data, taxCents: quoted.taxCents } }} testId={`cki-line-tax-${line.cartItemId}`} />
            : refusedTax ? <TaxQuoteValue quote={{ ...quote, tax: { ...quote.tax, taxCents: refusedTax.taxCents } }} testId={`cki-line-tax-${line.cartItemId}`} />
            : quote.status === "error" ? <TaxQuoteValue quote={{ ...quote, tax: null }} testId={`cki-line-tax-${line.cartItemId}`} />
            : <Spinner />}
        </td>
        <td className="cki-cell cki-cell--money cki-cell--total" data-label={headerLabels.total}>
          {!line.eligible || !isChosen ? <span className="cki-muted">—</span>
            : quoted ? formatUsdCents(quoted.amountCents)
            : quote.status === "error" ? <span className="cki-muted">—</span>
            : <Spinner />}
        </td>
      </tr>
    );
  };

  // A failed quote does not block here: the Checkout re-quotes (the delivery
  // address can still be chosen there) and blocks payment itself.
  const checkoutDisabled = !selectedLines.length || preparing || !shippingReady || quote.status === "loading" || Boolean(blockedLine);

  return (
    <div className="cki-entry">
      <button type="button" className="cki-btn cki-btn--primary cki-btn--block" onClick={openModal} data-testid="cart-checkin-button">
        <span aria-hidden="true">✓</span> {T("h_ckin_button", "Check-in")}
      </button>
      <p className="cki-entry__hint">{T("h_ckin_entry_hint", "Choose which products of your cart to pay now.")}</p>
      {pageNotice && <div className={`cki-alert cki-alert--${pageNotice.type}`} role="status">{pageNotice.text}</div>}

      <Modal show={open} onHide={() => setOpen(false)} size="xl" centered scrollable dialogClassName="cki-dialog" aria-labelledby="cki-title">
        <Modal.Header closeButton className="cki-header">
          <div>
            <Modal.Title id="cki-title" className="cki-title">{T("h_ckin_title", "Check-in")}</Modal.Title>
            <p className="cki-subtitle">{T("h_ckin_subtitle", "Select the products you want to pay now. The others stay in your cart.")}</p>
          </div>
        </Modal.Header>
        <Modal.Body className="cki-body">
          {state.status === "loading" && <div className="cki-state" role="status"><Spinner /> {T("h_ckin_loading", "Loading your cart…")}</div>}
          {state.status === "error" && (
            <div className="cki-state">
              <div className="cki-alert cki-alert--error" role="alert">{state.error || T("h_ckin_load_failed", "Your cart could not be loaded. Please try again.")}</div>
              <button type="button" className="cki-btn cki-btn--ghost" onClick={load}>{T("h_ckin_retry", "Try again")}</button>
            </div>
          )}
          {state.status === "ready" && lines.length === 0 && <div className="cki-state">{T("h_ckin_empty", "Your cart is empty.")}</div>}
          {state.status === "ready" && lines.length > 0 && (
            <>
              <div className="cki-toolbar">
                <label className="cki-selectall">
                  <SelectBox checked={allSelected} indeterminate={someSelected} disabled={!eligible.length} onChange={toggleAll} label={T("h_ckin_select_all", "Select all")} />
                  <span>{T("h_ckin_select_all", "Select all")}</span>
                </label>
                <span className="cki-count" aria-live="polite">
                  {T("h_ckin_selected_count", "{count} of {total} selected", { count: selectedLines.length, total: eligible.length })}
                </span>
              </div>
              {notice && <div className={`cki-alert cki-alert--${notice.type}`} role="alert">{notice.text}</div>}

              <div className="cki-table-wrap">
                <table className="cki-table">
                  <thead>
                    <tr>
                      <th scope="col" className="cki-cell--check">
                        <SelectBox checked={allSelected} indeterminate={someSelected} disabled={!eligible.length} onChange={toggleAll} label={T("h_ckin_select_all", "Select all")} />
                      </th>
                      <th scope="col" className="cki-cell--image"><span className="cki-sr">{T("h_ckin_col_image", "Image")}</span></th>
                      <th scope="col">{headerLabels.product}</th>
                      <th scope="col" className="cki-cell--money">{headerLabels.itemPrice}</th>
                      <th scope="col" className="cki-cell--money">{headerLabels.shipping}</th>
                      <th scope="col" className="cki-cell--money">{headerLabels.tax}</th>
                      <th scope="col" className="cki-cell--money">{headerLabels.total}</th>
                    </tr>
                  </thead>
                  <tbody>{lines.map(renderRow)}</tbody>
                </table>
              </div>

              <section className="cki-summary" aria-label={T("h_ckin_summary", "Summary")}>
                <div className="cki-summary__row">
                  <span>{T("h_ckin_item_total", "Item Total")}</span>
                  <span className="cki-summary__value" data-testid="cki-item-total">{formatMoney(itemTotal)}</span>
                </div>
                <div className="cki-summary__row">
                  <span>{headerLabels.shipping}</span>
                  {selectedLines.length ? <ShippingCostValue estimate={selectedShipping} /> : <span className="cki-summary__value">{formatMoney(0)}</span>}
                </div>
                <div className="cki-summary__row">
                  <span>{headerLabels.tax}</span>
                  {selectedLines.length
                    ? <TaxQuoteValue quote={quote} className="cki-summary__value" testId="cki-tax-total" />
                    : <span className="cki-summary__value" data-testid="cki-tax-total">{formatMoney(0)}</span>}
                </div>
                <div className="cki-summary__divider" />
                <div className="cki-summary__row cki-summary__row--total">
                  <span>{headerLabels.total}</span>
                  <span className="cki-summary__total" data-testid="cki-total" aria-live="polite">
                    {!selectedLines.length ? formatMoney(0) : quoteReady ? formatMoney(total) : quote.status === "error" ? "—" : <Spinner />}
                  </span>
                </div>
                {blockedLine && <div className="cki-alert cki-alert--error" role="alert">{blockedLineMessage(blockedLine, selectedShipping.data?.destination?.country)}</div>}
                {quote.status === "error" && quote.error && <p className="cki-hint" role="note">{quote.error}</p>}
                <TaxBasisNote quote={quote} className="cki-hint" />
                {!selectedLines.length && <p className="cki-hint">{T("h_ckin_no_selection", "No products selected")}</p>}
                <p className="cki-hint">{T("h_ckin_estimate_note", "Shipping and tax are calculated for your delivery address; Checkout recalculates them before payment.")}</p>
                <button type="button" className="cki-btn cki-btn--primary cki-btn--block" disabled={checkoutDisabled} onClick={startCheckout} data-testid="cki-checkout">
                  {preparing ? <><Spinner /> {T("h_ckin_preparing", "Preparing checkout…")}</> : <><span aria-hidden="true">🔒</span> {T("h_ckin_checkout", "Checkout")}</>}
                </button>
              </section>
            </>
          )}
        </Modal.Body>
      </Modal>

      {checkout && (
        // The existing Checkout, rendering only its own checkout modal, with
        // the selected lines only (same pattern as the Combined Offer Buy Now).
        <Checkout
          key={checkout.key}
          modalOnly
          onClose={() => { setCheckout(null); setOpen(true); }}
          Saddress={Saddress}
          setSAddressFinal={setSAddressFinal}
          addressSel={infoSource?.address}
          products={checkout.products}
          cart={true}
          owner={checkout.products[0]?.owner}
          addressShop={checkout.products[0]?.addressShop || {}}
          laguangeElakay={laguangeElakay}
          infoSource={infoSource}
          onOrderSuccess={onOrderSuccess}
        />
      )}
    </div>
  );
};

export default CartCheckin;
